// Surface 2 · Preview (/frameworks/{frameworkId}).
//
// Spec: specs/consumer-frameworks-strings.md § Surface 2 · Preview.
//
// Ordering decision (handoff §1 shaping decisions):
//   1. Hero (domain/maturity chips, title, lead, primary CTAs)
//   2. "What this moves" (maturity ladder + duration block)
//   3. "What you walk away with" → DELIVERABLES — leads the page
//   4. "The work" → phase/activity tree — read-only, pre-commit visibility
//   5. "Who you will need" → stakeholder pills (union across activities)
//   6. Bottom CTA repeat
//
// ActivityDrawer (clickable "open →" on each activity row) joins in MR 5
// — the same component drives both the preview-mode read-only drawer
// and the workspace-mode interactive one. For MR 3 the activity rows
// render all the useful inline info and the "open →" link is suppressed.
//
// Routing: `/frameworks/{frameworkId}`. Fetches via GET /me/frameworks/{id}.
// Spec uses `{slug}` — we use the Mongo id for now (flagged in
// consumer-mvp-plan.md § open items; slug can land as a cheap migration
// once the admin editor supports authoring one).

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  Activity,
  ActivityDependency,
  FrameworkDeliverable,
  GetMeFrameworkDetailResponse,
} from '@gc-coach/shared';
import { getMeFrameworkDetail } from '../api/endpoints.js';
import { useI18n } from '../i18n/index.js';
import { MaturityLadder } from './MaturityLadder.js';
import styles from './FrameworkPreviewPage.module.css';

export function FrameworkPreviewPage() {
  const { frameworkId } = useParams<{ frameworkId: string }>();
  const navigate = useNavigate();
  const { t, tt } = useI18n();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'notfound' }
    | { kind: 'ok'; data: GetMeFrameworkDetailResponse }
  >({ kind: 'loading' });

  useEffect(() => {
    if (!frameworkId) {
      setState({ kind: 'notfound' });
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await getMeFrameworkDetail(frameworkId);
      if (cancelled) return;
      if (!result.ok) {
        if (result.code === 'NOT_FOUND') {
          setState({ kind: 'notfound' });
        } else {
          setState({ kind: 'error', message: t('frameworks.library.errorGeneric') });
        }
        return;
      }
      setState({ kind: 'ok', data: result.data });
    })();
    return () => {
      cancelled = true;
    };
  }, [frameworkId, t]);

  if (state.kind === 'loading') {
    return (
      <div className={styles.pad}>
        <div className={styles.wrap}>
          <div className={styles.skeletonHero} />
          <div className={styles.skeletonCard} />
          <div className={styles.skeletonCard} />
        </div>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className={styles.pad}>
        <div className={styles.wrap}>
          <div className={styles.messagePanel}>
            <p>{state.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (state.kind === 'notfound') {
    return (
      <div className={styles.pad}>
        <div className={styles.wrap}>
          <div className={styles.messagePanel}>
            <h3 className={styles.messageTitle}>{t('frameworks.preview.notFoundTitle')}</h3>
            <p>{t('frameworks.preview.notFoundBody')}</p>
            <button
              type="button"
              className={styles.messageButton}
              onClick={() => navigate('/frameworks')}
            >
              {t('frameworks.preview.backToLibrary')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { framework, active_instance_id } = state.data;
  const isActive = active_instance_id !== null;

  // Union of stakeholders across every activity in every phase — the
  // "Who you will need" card renders the deduped set as pills.
  const stakeholderSet = new Set<string>();
  for (const phase of framework.phases) {
    for (const activity of phase.activities) {
      (activity.stakeholders ?? []).forEach((s) => stakeholderSet.add(s));
    }
  }
  const stakeholders = Array.from(stakeholderSet);

  const domainLabel = t(`frameworks.domain.${framework.domain}`);
  const target = framework.maturity_target?.level ?? null;

  // Resolve dependency labels for the "after X, Y" copy on each activity row.
  // Builds an id → title map across every activity in every phase.
  const activityById = new Map<string, Activity>();
  for (const phase of framework.phases) {
    for (const a of phase.activities) activityById.set(a.id, a);
  }

  return (
    <div className={styles.pad}>
      <div className={styles.wrap}>
        <Link to="/frameworks" className={styles.backLink}>
          ← {t('frameworks.preview.backToLibrary')}
        </Link>

        {/* ─── Hero ─────────────────────────────────────────────── */}
        <div className={styles.heroBadges}>
          <span className={styles.domainChip}>{domainLabel}</span>
          {target && (
            <span className={styles.maturityChip}>
              → {t(`frameworks.maturity.${target}`)}
            </span>
          )}
        </div>
        <h1 className={styles.title}>{framework.title}</h1>
        <p className={styles.lead}>{framework.short_description}</p>

        <div className={styles.ctaRow}>
          {/* MR 4 wires this to the ActivationModal. For MR 3 it routes to
            * /frameworks/{id}/activate — nonexistent route, falls to dashboard
            * via the router's catch-all until MR 4 lands. */}
          <button
            type="button"
            className={styles.ctaPrimary}
            onClick={() =>
              navigate(
                isActive
                  ? `/frameworks/instances/${active_instance_id}`
                  : `/frameworks/${framework.id}/activate`,
              )
            }
          >
            {isActive
              ? t('frameworks.preview.ctaResumeInstance')
              : t('frameworks.preview.ctaStartFramework')}
          </button>
          <Link to="/coach" className={styles.ctaSecondary}>
            {t('frameworks.preview.ctaAskCoach')}
          </Link>
        </div>

        {/* ─── What this moves ───────────────────────────────────── */}
        <section className={styles.movesCard}>
          <div className={styles.movesLeft}>
            <span className={styles.eyebrowMuted}>
              {t('frameworks.preview.whatThisMovesLabel')}
            </span>
            <p className={styles.movesBody}>
              {target
                ? tt('frameworks.preview.whatThisMovesBodyTemplate', {
                    current: t('frameworks.preview.currentUnknown'),
                    domain: domainLabel,
                    target: t(`frameworks.maturity.${target}`),
                  })
                : t('frameworks.preview.whatThisMovesBodyNoTarget')}
            </p>
            {target && (
              <MaturityLadder current={null} target={target} />
            )}
          </div>
          <div className={styles.movesRight}>
            <span className={styles.eyebrowMuted}>
              {t('frameworks.preview.timeLabel')}
            </span>
            <div className={styles.movesTimeValue}>
              {framework.estimated_effort_label ?? t('frameworks.preview.timeUnknown')}
            </div>
            <div className={styles.movesTimeNote}>
              {t('frameworks.preview.timeNote')}
            </div>
          </div>
        </section>

        {/* ─── Deliverables — the reason to buy ──────────────────── */}
        {framework.deliverables && framework.deliverables.length > 0 && (
          <>
            <div className={styles.sectionHeader}>
              <span className={styles.eyebrow}>
                {t('frameworks.preview.deliverablesEyebrow')}
              </span>
              <h2 className={styles.sectionTitle}>
                {tt('frameworks.preview.deliverablesTitleTemplate', {
                  n: String(framework.deliverables.length),
                })}
              </h2>
            </div>
            <div className={styles.deliverableCard}>
              {framework.deliverables.map((d, i) => (
                <DeliverableRow key={d.id} index={i + 1} deliverable={d} />
              ))}
            </div>
          </>
        )}

        {/* ─── The work — phases + activities, read-only ─────────── */}
        <div className={styles.sectionHeaderInline}>
          <span className={styles.eyebrowMuted}>
            {t('frameworks.preview.theWorkEyebrow')}
          </span>
          <span className={styles.note}>{t('frameworks.preview.theWorkNote')}</span>
        </div>
        <div className={styles.phaseList}>
          {framework.phases.map((phase, pi) => (
            <div key={phase.id} className={styles.phaseCard}>
              <div className={styles.phaseHeader}>
                <span className={styles.phaseNumber}>
                  {t('frameworks.preview.phaseLabel')} {pi + 1}
                </span>
                <span className={styles.phaseName}>{phase.title}</span>
                <span className={styles.phaseMeta}>
                  {phase.activities.length}{' '}
                  {t('frameworks.library.metricActivities').trim()}
                </span>
              </div>
              <div className={styles.phaseBody}>
                {phase.activities.map((a) => (
                  <ActivityRow
                    key={a.id}
                    activity={a}
                    dependencyTitles={(a.dependencies ?? []).map(
                      (dep: ActivityDependency) =>
                        activityById.get(dep.activity_id)?.title ?? dep.activity_id,
                    )}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* ─── Who you will need ─────────────────────────────────── */}
        {stakeholders.length > 0 && (
          <section className={styles.stakeCard}>
            <span className={styles.eyebrowMuted}>
              {t('frameworks.preview.stakeholdersLabel')}
            </span>
            <p className={styles.stakeBody}>{t('frameworks.preview.stakeholdersBody')}</p>
            <div className={styles.stakeChips}>
              {stakeholders.map((s) => (
                <span key={s} className={styles.stakeChip}>
                  {s}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* ─── Bottom CTA repeat ─────────────────────────────────── */}
        <div className={styles.ctaFooterRow}>
          <button
            type="button"
            className={styles.ctaPrimary}
            onClick={() =>
              navigate(
                isActive
                  ? `/frameworks/instances/${active_instance_id}`
                  : `/frameworks/${framework.id}/activate`,
              )
            }
          >
            {isActive
              ? t('frameworks.preview.ctaResumeInstance')
              : t('frameworks.preview.ctaStartFramework')}
          </button>
          {!isActive && (
            <span className={styles.note}>{t('frameworks.preview.ctaFooterNote')}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Deliverable row ──────────────────────────────────────────────────────

interface DeliverableRowProps {
  index: number;
  deliverable: FrameworkDeliverable;
}

function DeliverableRow({ index, deliverable }: DeliverableRowProps) {
  const { t } = useI18n();
  const [showRubric, setShowRubric] = useState(false);
  const hasRubric = Boolean(deliverable.evaluation_rubric);

  return (
    <div className={styles.deliverableRow}>
      <div className={styles.deliverableIndex}>{index}</div>
      <div className={styles.deliverableContent}>
        <div className={styles.deliverableName}>{deliverable.name}</div>
        <div className={styles.deliverableDesc}>{deliverable.description}</div>
        {hasRubric && (
          <>
            <button
              type="button"
              className={styles.deliverableRubricToggle}
              onClick={() => setShowRubric((v) => !v)}
            >
              {showRubric
                ? t('frameworks.preview.hideRubric')
                : t('frameworks.preview.showRubric')}
            </button>
            {showRubric && (
              <div className={styles.deliverableRubric}>
                {deliverable.evaluation_rubric}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Activity row ─────────────────────────────────────────────────────────

interface ActivityRowProps {
  activity: Activity;
  dependencyTitles: ReadonlyArray<string>;
}

function ActivityRow({ activity, dependencyTitles }: ActivityRowProps) {
  const { t } = useI18n();
  const producesDeliverable = (activity.documents ?? []).some(
    (d) => d.role === 'deliverable_template',
  );
  const duration = activity.duration?.default_months
    ? t(`frameworks.duration.${activity.duration.default_months}`)
    : null;

  return (
    <div className={styles.activityRow}>
      <span className={styles.activityDot} aria-hidden />
      <div className={styles.activityMain}>
        <div className={styles.activityName}>{activity.title}</div>
        <div className={styles.note}>
          {duration ?? ''}
          {duration && dependencyTitles.length > 0 ? ' · ' : ''}
          {dependencyTitles.length > 0 && (
            <>
              {t('frameworks.preview.afterLabel')} {dependencyTitles.join(', ')}
            </>
          )}
        </div>
      </div>
      {producesDeliverable && (
        <span className={styles.producesDeliverableChip}>
          {t('frameworks.preview.producesDeliverable')}
        </span>
      )}
    </div>
  );
}
